import React, { useContext, useRef } from "react";
import { UncontrolledCollapse } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStopwatch,
  faChevronCircleRight,
  faTimesCircle,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";
import { renderRatingByValue } from "../../services/ValueDataService";
import { renderRichText } from "src/services/PrismicService";
// payment logos
import AnalyticsService from "../../services/AnalyticsService";
import { SubscriptionContext } from "src/services/SubscriptionService";
import { getOfferId, OfferItem, splitParagraphs, StateItem } from "src/services/OfferService";
import styles from "./styles.scss";
import MobileReviewTooltip from "./MobileReviewTooltip";
import { useQuery } from "@apollo/client";
import { ALL_SPORTSBOOK_REVIEWS_QUERY } from "src/gql/queries";
import { useContainerQuery } from "src/services/UtilService";
import { AffiliateLink } from "src/services/OfferService";

const staffPickBadge = "/staff-pick-badge.png";
const paypal = "/logos/paypal.png";
const visa = "/logos/visa.jpg";
const mastercard = "/logos/mastercard.png";

const paymentLogos: any = { paypal, visa, mastercard };

const OfferLogo = ({ offer }: { offer: OfferItem }) => (
  // eslint-disable-next-line @next/next/no-img-element
  <img src={offer.image.url} alt="" className="offer-logo" />
);

const OfferLogoMobile = ({ offer }: { offer: OfferItem }) => (
  // eslint-disable-next-line @next/next/no-img-element
  <div style={{ position: "relative" }}>
    <OfferLogo offer={offer} />
    <NewBadge
      style={{
        position: "absolute",
        right: 0,
        bottom: 0,
        marginBottom: 0,
        fontSize: 10,
        width: 40,
        padding: 2,
      }}
      offer={offer}
    />
  </div>
);

const StaffPick = ({ offer }: { offer: OfferItem }) =>
  offer.staff_pick === "true" ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={staffPickBadge} alt="" className="staff-pick" />
  ) : null;

const NewBadge = ({ offer, style }: { offer: OfferItem; style?: any }) =>
  offer.new_badge === true ? (
    // eslint-disable-next-line @next/next/no-img-element
    <div className="new-badge" style={style}>
      NEW
    </div>
  ) : null;

const OfferRating = ({ offer }: { offer: OfferItem }) => (
  <div className="offer-rating-container">
    {renderRatingByValue(offer.rating)}
    <div className="offer-rating-text">{offer.rating.toFixed(1)}</div>
  </div>
);

const OfferClaimButton = ({
  offer,
  state,
  children = "Claim Offer",
  ...other
}: {
  offer: OfferItem;
  state: StateItem;
  [key: string]: any;
}) => (
  <AffiliateLink
    {...other}
    href={offer.web_link}
    target="_blank"
    rel="noopener nofollow"
    className="offer-claim-button"
    onClick={() => {
      AnalyticsService.track("Claim Offer", {
        sportsbook: offer.sportsbook_name,
        affiliateLink: offer.web_link,
        offerText: offer.welcome_offer,
        howText: offer.how_it_works,
        state: state.name,
        page: window.location.href,
        component: "OfferClaimButton",
      });
    }}
  >
    {children}
  </AffiliateLink>
);

const OfferText = ({ offer }: { offer: OfferItem }) => {
  const howItWorks = offer.how_it_works_rich
    ? renderRichText(offer.how_it_works_rich)
    : splitParagraphs(offer.how_it_works);
  return (
    <div>
      <div className="offer-description">{offer.welcome_offer}</div>
      {howItWorks && <div className="offer-rating-how-it-works">{howItWorks}</div>}
    </div>
  );
};

const OfferReviewButton = ({ offer, state }: { offer: OfferItem; state: StateItem }) => (
  <div
    id={`review-${getOfferId(offer)}`}
    className="offer-review-button"
    onClick={() => {
      AnalyticsService.track("Quick Review", {
        affiliateLink: offer.web_link,
        sportsbook: offer.sportsbook_name,
        offerText: offer.welcome_offer,
        howText: offer.how_it_works,
        state: state.name,
        page: window.location.href,
        component: "OfferReviewButton",
      });
    }}
  >
    <div style={{ fontWeight: 500 }}>
      READ QUICK{" "}
      <span style={{ whiteSpace: "nowrap" }}>
        REVIEW
        <FontAwesomeIcon
          icon={faChevronCircleRight}
          color="#004198"
          style={{
            fontSize: 14,
            marginLeft: 5,
            position: "relative",
            top: 1,
          }}
        />
      </span>
    </div>
    <div style={{ marginTop: 7 }}>
      <FontAwesomeIcon
        icon={faStopwatch}
        color="#aed7ff"
        style={{
          fontSize: 25,
          marginRight: 10,
          position: "relative",
          top: 3,
        }}
      />
      2 Minute Read
    </div>
  </div>
);

const Triangle = () => (
  <div>
    <svg
      className="triangle"
      height="20"
      width="30"
      style={{
        zIndex: 1,
        position: "relative",
        top: 2,
        left: "50%",
        transform: "translateX(-50%)",
      }}
    >
      <polygon points="0,20 30,20 15,0" style={{ fill: "#f1f8ff", stroke: "#aed7ff", strokeWidth: 1 }} />
      <line x1="0" y1="20" x2="30" y2="20" style={{ stroke: "#f1f8ff", strokeWidth: 2 }} />
    </svg>
  </div>
);

const OfferCloseButton = ({ offer }: { offer: OfferItem }) => (
  <div id={`review-${getOfferId(offer)}`} className="offer-review-close">
    <FontAwesomeIcon icon={faTimesCircle} color="#272a33" />
  </div>
);

const OfferPromo = ({ offer }: { offer: OfferItem }) => (
  <div className="offer-promo">
    <div className="offer-promo-border">
      <div className="offer-promo-label">PROMO CODE</div>
      <div className="offer-promo-code">{offer.promo_code}</div>
    </div>
  </div>
);

const OfferPayment = ({ offer, state }: { offer: OfferItem; state: StateItem }) => {
  const logos = (offer.recommended_payment || "")
    .split(",")
    .map((id: any) => paymentLogos[id])
    .filter((x: any) => x);

  return (
    <div className="offer-payment">
      <div className="offer-payment-title">
        <FontAwesomeIcon
          icon={faFileInvoiceDollar}
          color="#02a16c"
          style={{
            fontSize: 25,
            marginRight: 10,
            position: "relative",
            top: 3,
          }}
        />
        Payment Details
      </div>
      <div className="offer-payment-detail">Withdrawal Time: {offer.withdrawal_time || "None"}</div>
      <div className="offer-payment-detail">Minimum deposit: {offer.minimum_deposit || "None"}</div>
      <div className="offer-payment-detail">Minimum withdrawal: {offer.minimum_withdrawal || "None"}</div>
      <div className="offer-payment-divider" />
      {logos.length > 0 && (
        <>
          <div className="offer-payment-failing">Recommended Payment:</div>
          <div className="offer-payment-failing">Some payments are failing so we recommend you use:</div>
          <div className="offer-payment-logos">
            {logos.map((src: any, id: any) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img className="offer-payment-logo lazyload" key={id} data-src={src} />
            ))}
          </div>
        </>
      )}
      {offer.promo_code ? (
        <OfferPromo offer={offer} />
      ) : (
        <OfferClaimButton offer={offer} state={state} style={{ background: "#02a16c", border: "none", marginTop: 30 }}>
          Claim Offer Now
          <FontAwesomeIcon
            icon={faChevronCircleRight}
            color="white"
            style={{
              fontSize: 14,
              marginLeft: 5,
              position: "relative",
              top: 1,
            }}
          />
        </OfferClaimButton>
      )}
    </div>
  );
};

const OfferReviewContent = ({ offer }: { offer: OfferItem }) => (
  <div className="offer-review-body">{renderRichText(offer.review_content)}</div>
);

const OfferReview = ({ offer, state }: { offer: OfferItem; state: StateItem }) => (
  <UncontrolledCollapse toggler={`#review-${getOfferId(offer)}`} className="offer-review-toggler">
    <Triangle />
    <div className="offer-review">
      <OfferCloseButton offer={offer} />
      <OfferPayment offer={offer} state={state} />
      <OfferReviewContent offer={offer} />
    </div>
  </UncontrolledCollapse>
);

const hasReview = (offer: OfferItem) => Boolean(offer.review_content?.[0]?.text);

function OfferCard({ offer, state }: { offer: OfferItem; state?: StateItem }) {
  const ref = useRef<any>(null);
  useContainerQuery(ref, {
    small: 750,
    large: Infinity,
  });

  const { userLocale } = useContext(SubscriptionContext);
  const offerState = state ?? ({ name: userLocale } as StateItem);
  const sportsbookName = offer.sportsbook_name?.toLowerCase();

  const { data } = useQuery(ALL_SPORTSBOOK_REVIEWS_QUERY);
  const sportsbookReviewsByName = data?.prismicAllSportsbook_reviews?.edges.reduce((acc: any, edge: any) => {
    const reviewName = edge?.node?.sportsbook_name?.toLowerCase();
    const reviewSlug = edge?.node?._meta?.uid?.toLowerCase();

    if (reviewName && reviewSlug) {
      acc[reviewName] = reviewSlug;
    }
    return acc;
  }, {});

  const sportsbookReviewSlug = sportsbookName ? sportsbookReviewsByName?.[sportsbookName] : undefined;
  const hasSportsbookReview = Boolean(sportsbookReviewSlug);

  return (
    <div ref={ref} className="offer-card-container">
      <div className="offer-desktop-row mobile-disappear">
        <div className="offer-desktop-cell">
          <OfferLogo offer={offer} />
          <StaffPick offer={offer} />
        </div>
        <div className="offer-desktop-cell">
          <NewBadge offer={offer} />
          <OfferText offer={offer} />
        </div>
        <div className="offer-desktop-cell">
          <OfferRating offer={offer} />
          {hasReview(offer) && <OfferReviewButton offer={offer} state={offerState} />}
        </div>
        <div className="offer-desktop-cell">
          <OfferClaimButton offer={offer} state={offerState} />
          {hasSportsbookReview && (
            <a href={`/sportsbook-review/${sportsbookReviewSlug}`} className="offer-sportsbook-review-desktop">
              Sportsbook Review
            </a>
          )}
        </div>
        {hasReview(offer) && <OfferReview offer={offer} state={offerState} />}
      </div>

      <div className="offer-mobile-card desktop-disappear">
        <div className="offer-logo-w-rating">
          <OfferLogoMobile offer={offer} />
          <div className="offer-mobile-w-review">
            <OfferRating offer={offer} />
            {hasSportsbookReview && <MobileReviewTooltip offer={offer} sportsbookReviewSlug={sportsbookReviewSlug} />}
          </div>
        </div>
        <StaffPick offer={offer} />
        <OfferText offer={offer} />
        {hasReview(offer) && <OfferReviewButton offer={offer} state={offerState} />}
        {hasReview(offer) && <OfferReview offer={offer} state={offerState} />}
        <OfferClaimButton offer={offer} state={offerState} />
      </div>
      <style jsx>{styles}</style>
    </div>
  );
}

export default OfferCard;
